/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, HeadingSection, HFlow, Icon, Theme, Tooltip, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { PrivateRoute } from 'components/route'
import theme from 'config/theme'
import { useGrupoExameTableQuery } from 'graphql/hooks.generated'
import { GrupoExameQueryInput } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { emptyArray } from 'util/array'

import { EditGrupoExame } from './detail/EditGrupoExame'
import { GrupoExamesModalForm } from './detail/GrupoExamesModalForm'
import GrupoExameTable from './list/GrupoExameTable'

export function GrupoExameRootView() {
  const [params, setParams] = useState<GrupoExameQueryInput>({ pageParams: { page: { size: 30, sort: ['nome'] } } })
  const match = useRouteMatch()
  const styles = createStyles(theme)

  const iconToolTipText =
    'Os grupos de exames são apresentados no módulo de Solicitação de exames no Atendimento. ' +
    'Estes grupos são filtrados de acordo com idade e sexo do cidadão em atendimento.'

  const { data: { gruposExame } = {}, refetch, loading } = useGrupoExameTableQuery({ variables: { input: params } })

  const gruposExamesInativos = useMemo(() => {
    return gruposExame?.content.filter((grupo) => grupo.hasProcedimentosInativos) || emptyArray
  }, [gruposExame])
  const [showAlertGrupoExameInativo, setShowAlertGrupoExameInativo] = useState(true)

  const handleSortChange = (sort: string[]) => {
    setParams((state) => ({
      ...state,
      pageParams: { ...state.pageParams, page: { ...state.pageParams.page, sort } },
    }))
  }

  const renderCreate = () => {
    return <GrupoExamesModalForm refetch={refetch} />
  }

  const renderEdit = () => {
    return <EditGrupoExame refetch={refetch} />
  }

  return (
    <CheckPermission permission={Permissions.configuracaoMunicipal.cadastrarEditarEExcluirGruposDeExames}>
      <HeadingSection
        level={2}
        title={
          <HFlow hSpacing={0.5} alignItems='center'>
            Grupo de exames
            <Tooltip text={iconToolTipText} placement='auto'>
              <Icon icon='infoCircleFilled' size={1} style={styles.infoIcon} />
            </Tooltip>
          </HFlow>
        }
      />
      <VFlow>
        {gruposExamesInativos.length > 0 && showAlertGrupoExameInativo && (
          <Alert
            type='info'
            onCloseClick={() => setShowAlertGrupoExameInativo(false)}
            inline
            styles={{ wrapper: { padding: '0.5rem', width: '100%' } }}
          >
            <b>
              {gruposExamesInativos.length > 1
                ? 'Existem grupos com exames inativos que não estão mais disponíveis no atendimento. Edite os grupos para remover ou substituir esses exames.'
                : 'Existe um grupo com exames inativos que não estão mais disponíveis no atendimento. Edite o grupo para remover ou substituir esses exames.'}
            </b>
            <br />
            {gruposExamesInativos.length > 1 ? 'Grupos afetados:' : 'Grupo afetado:'}
            <ul css={styles.alertListItem}>
              {gruposExamesInativos.map((grupo) => (
                <li key={grupo.id}>{grupo.nome.capitalize()}</li>
              ))}
            </ul>
          </Alert>
        )}
        <GrupoExameTable
          gruposExame={gruposExame}
          loading={loading}
          refetch={refetch}
          sort={params.pageParams.page.sort}
          onSortChange={handleSortChange}
        />
        <PrivateRoute
          exact
          path={`${match.url}/create`}
          render={renderCreate}
          permission={Permissions.configuracaoMunicipal.cadastrarEditarEExcluirGruposDeExames}
        />
        <PrivateRoute
          exact
          path={`${match.url}/edit/:grupoExameId(\\d+)`}
          render={renderEdit}
          permission={Permissions.configuracaoMunicipal.cadastrarEditarEExcluirGruposDeExames}
        />
      </VFlow>
    </CheckPermission>
  )
}

const createStyles = (theme: Theme) => ({
  infoIcon: css`
    &:hover {
      color: ${theme.pallete.primary.main};
    }
  `,
  alertListItem: css`
    margin: 0;
    padding-left: 1.5rem;
  `,
})
